import moment from 'moment';
import ExportChartFlag from './ExportChartFlag';
import ExportChartLogo from 'assets/general/Logo_BMI_Geo_Risk_Signals_White.svg?react';
import { FGTypography } from '@fg/design-system';
import {
    HEATMAP_RISK_TYPE_TO_TITLE,
    HEATMAP_SCORE_TYPE_TO_TITLE
} from 'components/Heatmap/HeatmapTypes';
import {
    MOMENTJS_DATE_DISPLAY_FORMAT,
} from '../../../constants';
import { FREQUENCY_VIEWS, IExportChartConfig, VIEW } from 'interfaces';
import { isSingleCountryMode, isContingentMode } from '../templateUtils';
import { getRiskLongName } from 'utils/riskUtils';

const labelValueStyle = {
    fontSize: '12px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    textTransform: 'capitalize',
    color: 'var(--fg-color-text)',
};

interface Props {
    chartConfig: IExportChartConfig,
}

const lOGO_HEIGHT = 24;

const ExportChartHeader = ({ chartConfig } : Props) => {
    if (chartConfig.viewType === VIEW.HEATMAP) {
        return renderHeatmapHeader(chartConfig);
    }

    const snapshotDate = moment().format(MOMENTJS_DATE_DISPLAY_FORMAT);

    const hasDateRange = true;
    const rangeLeft = moment(chartConfig.baseDate).diff(
        moment(chartConfig.startDate),
        'days'
    );
    const rangeRight = moment(chartConfig.endDate).diff(
        moment(chartConfig.baseDate),
        'days'
    );

    const dateStr = hasDateRange
        ? `${moment(chartConfig.baseDate)
              .subtract(rangeLeft, 'd')
              .format(MOMENTJS_DATE_DISPLAY_FORMAT)} - ${moment(
              chartConfig.baseDate
          )
              .add(rangeRight, 'd')
              .format(MOMENTJS_DATE_DISPLAY_FORMAT)}`
        : moment(chartConfig.virtualToday).format(MOMENTJS_DATE_DISPLAY_FORMAT);

    const singleCountryMode = isSingleCountryMode(chartConfig);
    const contingentMode = isContingentMode(chartConfig);
    let headerRow;
    let headerItems = [];

    if (singleCountryMode || contingentMode) {
        const countryId = chartConfig.countries[0];
        const countryDisplayData =
            chartConfig.displayNames.countries[countryId];

        headerRow = (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                }}>
                <ExportChartFlag
                    className="flag-icon-squared"
                    countryId={countryId}
                    country={countryDisplayData}
                />
                <FGTypography
                    variant="headline4"
                    tag="h4"
                    className="gq-template-component-header-text-v2">
                    {countryDisplayData.name}
                </FGTypography>
            </div>
        );

        headerItems = headerItems.concat([
            { label: 'Snapshot date', value: snapshotDate },
            { label: 'Timeframe', value: dateStr },
        ]);
    } else {
        const firstRiskDisplayName = getRiskLongName(
            chartConfig.riskType,
            chartConfig.riskIds[0]
        );
        headerRow = (
            <FGTypography
                variant="headline4"
                tag="h4"
                className="gq-template-component-header-text-v2">
                {firstRiskDisplayName}
            </FGTypography>
        );

        headerItems = headerItems.concat([
            { label: 'Snapshot date', value: snapshotDate },
            { label: 'Timeframe', value: dateStr },
        ]);
    }

    if (FREQUENCY_VIEWS.includes(chartConfig.viewType)) {
        headerItems.push({ label: 'Frequency', value: chartConfig.frequency });
    }
    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                {headerRow}
                <ExportChartLogo style={{height: lOGO_HEIGHT}} />
            </div>
            <div style={{ height: '15px', width: '100%' }}></div>
            { headerItems.map(({ label, value }) => (
                <div
                    key={label}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                    <div
                        style={{
                            width: '100px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                        }}>
                        <FGTypography variant="label3" tag="label">
                            {label}
                        </FGTypography>
                    </div>
                    <div
                        style={labelValueStyle}>
                        <FGTypography variant="label3" tag="label">
                            {value}
                        </FGTypography>
                    </div>
                </div>
            ))}
            <div style={{ height: '10px', width: '100%' }}></div>
        </>
    );
};

export const renderHeatmapHeader = (chartConfig: IExportChartConfig) => {
    const heatmap = chartConfig.heatmap;
    const dateStr = `${moment(heatmap.startDate).format(
        MOMENTJS_DATE_DISPLAY_FORMAT
    )} (${heatmap.timeSpan})`;

    return <>
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
            <FGTypography
                variant="headline4"
                tag="h4"
                className="gq-template-component-header-text-v2">
                {HEATMAP_RISK_TYPE_TO_TITLE[chartConfig.riskType]}
            </FGTypography>
            <ExportChartLogo style={{height: lOGO_HEIGHT}} />
        </div>
        <div
            style={{
                marginTop: '5px',
                display: 'flex',
                justifyContent: 'flex-start',
                margin: '5px 0px',
            }}>
            <div
                style={{
                    fontSize: '12px',
                    width: '130px',
                }}>
                Date
            </div>
            <div style={{ color: 'white', fontSize: '13px' }}>{dateStr}</div>
        </div>
        <div
            style={{
                marginTop: '5px',
                display: 'flex',
                justifyContent: 'flex-start',
                margin: '5px 0px',
            }}>
            <div
                style={{
                    fontSize: '12px',
                    width: '130px',
                }}>
                Score Type
            </div>
            <div style={{ color: 'white', fontSize: '13px' }}>
                {HEATMAP_SCORE_TYPE_TO_TITLE[heatmap.scoreType]}
            </div>
        </div>
    </>
};

export default ExportChartHeader;